// src/utils/PrintingUtils.js
import React from 'react';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import { translateDynamic, useLanguage, getTransliteration } from '../context/LanguageContext';
import { shouldPrintAtAll, shouldPrintDirect, DIRECT_PRINT_PROTOCOLS } from './printRouting';
import { sendToPrinter } from './eposClient';
import { sendPayload } from './printTransport';
import { tableSlotKind } from './tableSlot';
import { printingService } from '../services/api';
import tenantStorage from '../services/tenantStorage';
import { getStationConfig } from '../station/stationConfig';
import { starMarkupToHtml } from './starMarkupToHtml';
import { printedAtStamp } from './printedAtStamp';
import { ticketRoleCandidates } from './ticketRoles';

// Map a printable category name to a USB printer role (kitchen vs bar).
// Unclear categories return null → default printer (byte-identical for current tenants).
const roleForCategory = (category) => {
    if (!category) return null;
    const c = String(category).toLowerCase();
    if (c.includes('bar') || c.includes('drink') || c.includes('beverage') || c.includes('coffee')) return 'bar';
    if (c.includes('kitchen') || c.includes('food')) return 'kitchen';
    return null;
};

// Which printer roles could serve one KDS category's TICKET, best first —
// see utils/ticketRoles.js, which StationSettingsPrinter shares so the sheet
// describes the routing this file performs.
//
// This is deliberately NOT `roleForCategory` above. That one is a substring
// heuristic for routing the receipts[] buckets a sale produces, and it must
// keep its current behaviour (see roleForReceipt). This one answers "which
// printer does the Grill station's docket come out of".
//
// Every category gets its own printer if the shop has given it one, and the
// shared `kitchen` printer if it has not. There is no closed set and no
// special case — QC 2026-09-09: `bar` used to resolve to the single
// candidate ['bar'] with no fallback, so a shop whose only ticket printer
// was role='kitchen' printed NOTHING for a Bar item while still writing the
// order_tracking row. The KDS screen showed the order and no paper existed.

// Map a receipt bucket (from the receipts[] structure) to a USB printer role.
// Customer/Cashier copies are the receipt role; anything else is a category ticket.
const roleForReceipt = (category) => {
    if (category === 'Customer' || category === 'Cashier') return 'receipt';
    return roleForCategory(category);
};

const getReceiptTransliteration = (productName, receiptTranslationLanguage, dynamicTranslations, translationsLoaded) => {
    if (!receiptTranslationLanguage || !translationsLoaded || !dynamicTranslations) {
        console.log('Missing requirements for translation');
        return null;
    }

    // Try direct lookup first
    let translationData = dynamicTranslations[productName];

    // If not found, try without " - Add on" suffix for add-ons
    if (!translationData && productName.includes(' - Add on')) {
        const baseAddonName = productName.replace(' - Add on', '');
        translationData = dynamicTranslations[baseAddonName];
    }

    console.log('Translation data for', productName, ':', translationData);

    if (!translationData) {
        console.log('No translation data found');
        return null;
    }

    const translation = translationData[receiptTranslationLanguage];
    console.log('Translation result:', translation);

    return translation && translation !== productName ? translation : null;
};

// Function to safely get numeric values and prevent undefined
const safeNumeric = (value, defaultValue = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
};

// Epson direct-print wiring (F-epson Task 6).
//
// `protocol` lives per-printer (PrinterPublicOut.protocol), not as a single
// tenant-wide config value — a tenant can have a cloudprnt receipt printer
// and an epson kitchen printer at once. So the protocol that matters for a
// given print call is the one on the printer actually assigned to `role`,
// looked up fresh via printingService.listPrinters(). Only bothered for
// network-method tenants: for usb/none/unset, protocol never changes the
// outcome (shouldPrintAtAll/shouldPrintDirect are both method-gated first),
// so skip the network round trip entirely.
// `printers`, when passed, is an already-fetched printer list (from a single
// listPrinters() call made once by the outer print action) — reused instead
// of fetching again. Callers that print exactly one thing per action (the
// common case) can omit it and this fetches its own, same as before.
// Roles that may take the LAN-direct Epson path.
//
// 'receipt' dials GET /printing/receipt-payloads (renders every copy of the
// receipt off a `transaction_id` — the customer's plus a cashier copy per
// payment method). 'kitchen'/'bar' dial the ticket-side sibling GET
// /printing/epson/ticket-payload (Device Printer Config Task 8 — renders
// `render_ticket` off a `tracking_id`, since a kitchen/bar ticket fires when
// an order is sent to the kitchen, before any transaction exists). See
// attemptDirectEpsonPrint below for which identifier each role reads off
// `config`.
const DIRECT_PRINT_ROLES = ['receipt', 'kitchen', 'bar'];

// How long to wait on the printer-list lookup before giving up and
// treating the tenant as cloudprnt. This call sits in front of EVERY
// network-tenant print decision, including CloudPRNT tenants that gain
// nothing from it, and the backend is known to cold-start — an untimed
// call would stall the till for the whole connection lifetime. Falling
// back to 'cloudprnt' on timeout is the safe answer: the backend already
// queued this receipt's job, so nothing is lost.
const LIST_PRINTERS_TIMEOUT_MS = 2000;

// Device Printer Config Task 2: pick the printer that should serve `role`.
//
// If this device has an assigned `printerId` (station config, Task 1) AND
// that printer still exists, is active, and matches `role`, use it. Any of
// those conditions failing is treated exactly like no assignment at all —
// it falls through to `list.find((p) => p.role === role && p.is_active)`,
// the SAME expression this codebase used before this task, unchanged. That
// fallback line is deliberately not duplicated or reworded anywhere else:
// every caller of this function reaches it through this one return, so an
// unassigned device (every device in production today) cannot drift from
// its pre-Task-2 behaviour.
const resolveDevicePrinter = (printers, role, printerId) => {
    const list = printers || [];
    if (printerId) {
        const assigned = list.find((p) => p.id === printerId && p.role === role && p.is_active);
        if (assigned) return assigned;
    }
    return list.find((p) => p.role === role && p.is_active);
};

// Device Printer Config Task 7: a device can now be assigned a printer PER
// ROLE (`printerIds: { receipt, kitchen, bar }`) instead of a single
// tenant-wide printerId — the common layout is one till driving a receipt,
// kitchen, AND bar printer, none of which fit in one id.
//
// Resolution order:
//   1. printerIds[role], if this device has one set for the role being
//      resolved.
//   2. else the legacy scalar `printerId` — devices in the field before
//      this task have only this. It is intentionally NOT role-scoped here:
//      resolveDevicePrinter above already refuses an id whose printer.role
//      doesn't match `role`, so a legacy receipt-printer id offered up for
//      a kitchen lookup simply fails that match and falls through to the
//      tenant+role lookup, exactly like no assignment at all. That
//      self-filtering is what makes it safe to keep this fallback flat
//      rather than re-deriving each legacy id's role here too.
//   3. else null -> resolveDevicePrinter's tenant+role fallback, unchanged.
//
// Never throws on a config that predates printerIds (or is missing
// entirely) — every access below is optional-chained/defaulted.
const resolveAssignedPrinterId = (stationConfig, role) => {
    if (!stationConfig) return null;
    const fromMap = stationConfig.printerIds ? stationConfig.printerIds[role] : null;
    if (fromMap) return fromMap;
    return stationConfig.printerId || null;
};

// Device Printer Config Task 4 (fix round): resolveNetworkProtocol now
// returns { protocol, printerId } instead of a bare protocol string.
// `printerId` is the SAME printer that decided `protocol` — it is only
// ever the device's assigned printerId, and only when resolveDevicePrinter
// actually matched it (the "assigned" branch). If the assignment was
// missing, stale (deleted/inactive), or for a different role, `printerId`
// is null here too, exactly like protocol falls back to the tenant+role
// lookup. This is deliberate: it is the single source of truth threaded
// into attemptDirectEpsonPrint below so the protocol decision (direct vs
// queue) and the actual dial target can never point at two different
// physical printers — see printWithIframe/printMultipleReceipts, which
// pass this printerId straight through unchanged.
const resolveNetworkProtocol = async (method, role, printers = null) => {
    if (method !== 'network' || !role) {
        return { protocol: 'cloudprnt', printerId: null };
    }
    try {
        const list = printers || (await printingService.listPrinters({ timeout: LIST_PRINTERS_TIMEOUT_MS }));
        // Station config is a local read (Capacitor Preferences / localStorage
        // via getStationConfig) — never a network call — so it cannot add a
        // round trip or change timing here. It is contractually never
        // throwing (Task 1), but the .catch is belt-and-braces: a station
        // config read failing must degrade to "no assignment", i.e. the
        // exact pre-Task-2 lookup below, not a print failure.
        const stationConfig = await getStationConfig().catch(() => null);
        const assignedPrinterId = resolveAssignedPrinterId(stationConfig, role);
        const printer = resolveDevicePrinter(list, role, assignedPrinterId);
        // No active printer for this role at all -> nothing to dial directly.
        const protocol = printer?.protocol || 'cloudprnt';
        // Only surface the assigned id when it was actually the printer
        // that decided `protocol` (resolveDevicePrinter's assigned branch
        // matched). A fallback-resolved printer's id is never sent onward —
        // an unassigned device must produce printerId: null here, matching
        // Task 3's backend "omitted printer_id" contract exactly.
        const resolvedPrinterId = (assignedPrinterId && printer?.id === assignedPrinterId) ? assignedPrinterId : null;
        // `role` is no longer a closed set. A printer's role may hold a
        // category name ("Grill") so a shop can give its grill station its
        // own paper, so this can no longer reject anything outside
        // DIRECT_PRINT_ROLES — that whitelist would refuse every
        // per-category printer.
        //
        // Nothing is lost by dropping it: the guard only ever mattered when
        // NO printer matched the role, and resolveDevicePrinter already
        // expresses that by returning undefined, which leaves `protocol` at
        // its 'cloudprnt' default and stops shouldPrintDirect below. The
        // check was a second expression of the same fact.
        if (!printer) {
            return { protocol: 'cloudprnt', printerId: null };
        }
        return { protocol, printerId: resolvedPrinterId };
    } catch (err) {
        // Covers a timeout too — axios rejects on `timeout`, same as any
        // other request failure, so one catch already handles both.
        console.warn('Epson routing: printer lookup failed, defaulting to cloudprnt:', err);
        return { protocol: 'cloudprnt', printerId: null };
    }
};

// Attempts one direct-to-printer Epson print for `role`. Never throws and
// never rejects — a print must never take the till down. On any failure
// (missing identifier, payload fetch error/404, unreachable printer), this
// simply returns: the backend already queued this print's job automatically
// (enqueue_receipt_for_sale at transaction-creation time for a receipt,
// enqueue_ticket_for_order at OrderTracking-creation time for a ticket), so
// there is nothing further to enqueue here and nothing is silently lost.
// `printerId`, when given, is the exact printer resolveNetworkProtocol
// already resolved (via the device's station-config assignment) to decide
// `protocol === 'epson'` in the first place — see resolveNetworkProtocol's
// comment. Passing it through closes the gap where the protocol decision
// and the actual LAN dial target could resolve to two different printers:
// both now key off this one id. `undefined`/`null` means the device has no
// (matching) assignment, in which case the payload fetch must be called
// exactly as before Task 3/4 — no `printer_id` sent — so the backend's own
// byte-identical fallback lookup (oldest active printer for tenant+role)
// runs unchanged.
//
// Device Printer Config Task 8: 'receipt' and 'kitchen'/'bar' fetch two
// different documents off two different source records, so they read two
// different identifiers off `config` — 'receipt' reads `transaction_id`
// (a receipt is only ever printed once a sale exists); 'kitchen'/'bar' read
// `tracking_id` (a ticket fires when the order is sent to the kitchen,
// before any transaction exists, off an OrderTracking record). Neither
// identifier is guessed from the other — a caller that only has a
// transaction_id (no tracking_id in `config`) safely no-ops for a
// kitchen/bar role rather than fetching the wrong thing.
// One payload → one printer → one job result. Extracted from
// attemptDirectEpsonPrint's copy loop so the refund slip (printRefundSlip
// below) reports its job the same way rather than growing a second, subtly
// different copy of this — a PrintJob that is never reported stays "queued"
// forever in the portal's job list, which is the tenant's only diagnostic
// surface.
//
// Never throws: a printer being off must not take the caller down. Both the
// transport failure and the report failure are swallowed after logging,
// exactly as they were inline.
const sendPayloadAndReport = async (payload, role) => {
    try {
        const result = await sendPayload(payload);
        try {
            // `code` is the device-level ePOS error code, which is null for
            // every failure that never reached the device (timeout,
            // network_error, http_error, invalid_response). Falling back to
            // `reason` keeps a real diagnostic on the job record instead of
            // the backend writing fail_reason = "unknown" for all of them —
            // the portal job list is the tenant's only diagnostic surface.
            await printingService.reportJobResult(payload.job_id, {
                success: result.success,
                code: result.code || result.reason || null,
            });
        } catch (reportErr) {
            console.warn('Epson direct print: failed to report job result (job stays queued):', reportErr);
        }
    } catch (err) {
        console.warn(`Epson direct print failed for role "${role}", falling back to the queue:`, err);
    }
};

const attemptDirectEpsonPrint = async (config, role, printerId = null) => {
    if (!role) {
        console.warn('Epson direct print: no role given, skipping (falls back to the queue)');
        return;
    }

    // `role` is an open set now: 'receipt', or the name of any printable
    // category ("Grill", "PIRI'S BURGER COLLECTION"). It used to be checked
    // against the closed DIRECT_PRINT_ROLES list, which would reject every
    // per-category printer. Eligibility is already decided upstream — the
    // caller only reaches here once resolveNetworkProtocol matched a real
    // active printer for this role — so re-deriving it from a hardcoded list
    // was both redundant and, now, wrong.
    //
    // Everything that is not the customer receipt is a ticket, and reads its
    // identifier off the OrderTracking record rather than the transaction.
    const isTicketRole = role !== 'receipt';
    const identifier = isTicketRole ? config?.tracking_id : config?.transaction_id;
    if (!identifier) {
        console.warn(`Epson direct print: no ${isTicketRole ? 'tracking_id' : 'transaction_id'} on config for role "${role}", skipping (falls back to the queue)`);
        return;
    }

    // A ticket is one docket. A receipt is a SET: the customer's copy plus a
    // cashier copy per payment method, which is what the browser has always
    // printed. Sending only the first is why a split produced one receipt on
    // the app and three in the browser.
    let payloads;
    try {
        if (isTicketRole) {
            // The same language and local print moment the receipt path
            // already sends. Item 18: render_ticket has always accepted
            // translations, but this call site sent no language and the
            // endpoint had no parameter to receive one — so the kitchen, whose
            // staff are the people in the building most likely to want Arabic,
            // got the untranslated half of the product.
            //
            // `printedAtStamp()` is the ticket's ONLY clock. The tracking
            // row's submitted_at is UTC and the backend has no tenant timezone
            // to convert it with, so without this the ticket prints no date at
            // all rather than one an hour out.
            const language = await receiptLanguage();
            const ticket = await printingService.getEposTicketPayload(
                role, identifier, printerId || undefined, language, printedAtStamp(),
            );
            payloads = [ticket];
        } else {
            const language = await receiptLanguage();
            // The `Printed:` stamp. Taken here, once, immediately before the
            // request: this is the moment the till pressed print, and it is
            // the only local clock in the system — the backend's is UTC and
            // it has no tenant timezone to convert with, so it will not
            // invent one. Every copy the request returns shares this moment.
            const response = await printingService.getReceiptPayloads(
                identifier, printerId || undefined, language, printedAtStamp(),
            );
            payloads = response?.payloads || [];
        }
    } catch (err) {
        // No payload means no PrintJob was created and nothing was sent, so
        // this role provably printed nothing. Reported to the caller (false)
        // so a category ticket can fall back to the shared kitchen printer —
        // the case a staged rollout produces, where a backend does not yet
        // accept a category name as a role and answers 400.
        console.warn(`Epson direct print: no payload for role "${role}", falling back:`, err);
        return false;
    }

    if (!payloads.length) {
        // Nothing was rendered, so nothing was sent and no job exists. Same
        // contract as a failed fetch: provably nothing printed.
        console.warn(`Epson direct print: no copies returned for role "${role}"`);
        return false;
    }

    // Each copy is sent and reported independently. A jam on the cashier
    // copy must not stop the customer's, and must not mark it failed — the
    // job rows are separate for exactly this reason.
    for (const payload of payloads) {
        await sendPayloadAndReport(payload, role);
    }
    // A payload WAS obtained, so a PrintJob exists and the printer may well
    // have printed even if the response never came back. Reported as handled
    // so the caller does NOT also print on the fallback printer — one set of
    // items must never produce two dockets.
    return true;
};

// Device Printer Config Task 8 (caller wiring) — fires the direct-to-printer
// Epson ticket print for one printable category, completely independent of
// any local/iframe print path. The order-tracking dispatch functions this is
// called from (transactionFlow.js's dispatchKitchenTickets, drawerFlow.js's
// buildTrackingRecords) never printed anything themselves before this task —
// only the backend's enqueue_ticket_for_order (OrderTracking-creation hook)
// did. So this helper must ONLY ever reach for the network-Epson-direct
// branch and do nothing else: no local silent print, no iframe/print-dialog
// fallback for usb/none-method tenants — either would be new, untested
// printing behaviour for every existing tenant, not what this task asked
// for. `roleForCategory` returning null (category isn't kitchen/bar) is a
// safe no-op. `resolveNetworkProtocol` already folds in the device's
// per-role assignment (resolveAssignedPrinterId, Task 7) with a tenant+role
// fallback (Task 2, unchanged) — a device/tenant with no matching active
// Epson printer for `role` resolves to protocol 'cloudprnt' and
// shouldPrintDirect is false, so nothing happens: identical to today's
// behaviour, which never printed a ticket at all. Never throws — a kitchen
// printer being off must never block a sale or an order hitting the KDS.
const printCategoryTicket = async (category, trackingId, tenantConfig) => {
    try {
        // Any category the shop flagged for the kitchen display prints a
        // ticket. Reaching this function is itself the consent: the callers
        // (transactionFlow.dispatchKitchenTickets, drawerFlow's tracking
        // build, OrderHelpers' append path) only ever pass a category drawn
        // from `printableCategories`, which is exactly the set where
        // `is_kitchen_display` is true (useProductCatalog.js:42).
        //
        // This replaces two gates that between them made the feature
        // unreachable, and both were wrong:
        //
        //   * `split_category_printing` — a config key with no UI anywhere.
        //     No tenant in production has ever had it set, so no tenant has
        //     ever printed a ticket. A switch nobody can reach is not
        //     consent; the Kitchen Display toggle the shop already sets is.
        //
        //   * a closed {kitchen, bar} category map, mirrored from
        //     printing_service.enqueue_ticket_for_order. The mirroring was
        //     mistaken: this path does NOT go through that hook. It fetches
        //     from GET /printing/epson/ticket-payload, which applies no
        //     category gate at all — only role validation and a printer
        //     lookup. Real shops name categories "PIRI'S BURGER COLLECTION",
        //     never "kitchen", so the map rejected every real category.
        //
        // The last remnant of that closed map — 'bar' resolving to ['bar']
        // and nothing else — was removed on 2026-09-09 after staging QC. An
        // explicit "Bar" category still PREFERS a bar printer, but it now
        // falls back to the kitchen printer like every other category
        // instead of silently printing nothing. See ticketRoleCandidates.
        //
        // The method === 'network' gate is unchanged: a usb/none tenant has
        // no direct-dial transport and must stay untouched.

        // The More-menu "Kitchen/Bar Printing" switch. It is read here, not
        // threaded from the callers, because all three ticket dispatch paths
        // (cashier transactionFlow, waiter drawerFlow, OrderHelpers append)
        // funnel through this function and none of them has the value in
        // scope — it is tenant STATE, not tenant config.
        //
        // Until KDS tickets actually printed, this switch only gated a branch
        // of the RECEIPT path, so its label was already wrong and nobody
        // could tell. Now that it controls paper, it has to mean what it
        // says: a shop's only other way to stop tickets is to unassign the
        // printer, which would also strip items off the KDS screen.
        //
        // Unset reads as ON, matching how the menu renders it, and a failed
        // state read also prints: losing the kitchen's ticket because tenant
        // storage hiccuped is far worse than one unwanted docket.
        try {
            if ((await tenantStorage.getState('printingEnabled')) === false) return;
        } catch (stateErr) {
            console.warn('Ticket print: could not read printingEnabled, assuming on:', stateErr);
        }

        const method = tenantConfig?.printing?.method;

        // Only network tenants have a direct-dial transport. Gating here as
        // well as inside resolveNetworkProtocol is what keeps the printer
        // list fetch below off a usb/none tenant's path entirely.
        if (method !== 'network') return;

        // ONE printer-list fetch per ticket, shared by every candidate. This
        // sits on the post-payment path in front of a backend known to
        // cold-start, so letting each candidate fetch its own list would
        // multiply the till's worst case by the length of the chain.
        //
        // A failed/timed-out fetch means no printer is knowable, so no direct
        // print is possible — exactly what resolveNetworkProtocol's own catch
        // concludes. Returning here says the same thing without three more
        // round trips to learn it again.
        let printers;
        try {
            printers = await printingService.listPrinters({ timeout: LIST_PRINTERS_TIMEOUT_MS });
        } catch (err) {
            console.warn(`Ticket print: printer lookup failed for category "${category}":`, err);
            return;
        }

        // Route to the category's own printer when the shop has given it one
        // — a grill station and a dessert station want separate paper — and
        // fall back to the shared `kitchen` printer when it has not. See
        // ticketRoleCandidates for the order and why the raw name comes first.
        //
        // The fallback is what makes this safe to ship: a tenant with a
        // single role='kitchen' printer and no per-category ones behaves
        // exactly as it did before, so nothing changes for them until they
        // choose to add one. A category with neither prints nothing, which
        // resolveNetworkProtocol already expresses by returning a protocol
        // shouldPrintDirect rejects.
        for (const role of ticketRoleCandidates(category)) {
            const { protocol, printerId } = await resolveNetworkProtocol(method, role, printers);
            if (!shouldPrintDirect(method, protocol)) continue;
            const handled = await attemptDirectEpsonPrint({ tracking_id: trackingId }, role, printerId);
            // `false` means the payload could not be fetched — nothing was
            // enqueued and nothing was sent — so the next candidate is a
            // genuine fallback rather than a second docket.
            if (handled) return;
        }
    } catch (err) {
        console.warn(`Kitchen/bar ticket print skipped for category "${category}":`, err);
    }
};

// The customer's refund slip — the ticket-print sibling for money going the
// other way.
//
// GET /printing/epson/refund-payload and render_refund have both existed and
// worked since the refund feature shipped; api.js even DEFINED
// getRefundPayload. Nothing anywhere called it, so in production a refund
// created a correct Refund row, returned 201, and produced no paper and no
// PrintJob at all — the customer walked away with money back and no record
// of it. Verified on staging 2026-09-08 (refund RF95AA99BB81): zero bytes
// reached the printer, and no print request of any kind followed the 201.
//
// Role is 'receipt', and that is not a preference — the endpoint resolves
// `get_epson_printer(..., "receipt", ...)` itself and 404s if the printer
// handed to it has any other role. A refund slip is a customer/audit
// document and belongs on the same paper as the receipt it reverses.
//
// It deliberately does NOT go through attemptDirectEpsonPrint. That
// function's role === 'receipt' branch reads `config.transaction_id` and
// fetches getReceiptPayloads — which would reprint the ORIGINAL SALE, the
// one document a customer must not be handed after a refund. A refund has
// both ids, and the wrong one resolves to a perfectly valid, perfectly
// wrong document. The identifier the payload endpoint actually keys on is
// `refund_id` (Refund.refund_id); it derives the sale from the refund row
// itself.
//
// Never throws and never rejects: the refund is already committed on the
// server before this is called, so a printer being off, unreachable or
// unassigned must leave the refund exactly as it is. Same start-don't-await
// contract as printCategoryTicket — see utils/refundPrintDispatch.js.
const printRefundSlip = async (refundId, tenantConfig) => {
    try {
        // Only reachable post-creation: refund_id is minted by the server.
        // A caller that fires before POST /refunds resolves has nothing to
        // print, which is the trap that kept staff-meal tickets off paper.
        if (!refundId) {
            console.warn('Refund slip: no refund_id, skipping');
            return;
        }

        // The More-menu printing switch, read here rather than threaded, for
        // the same reason printCategoryTicket reads it: it is tenant STATE
        // and neither till shell has it in scope on the refund path. Unset
        // reads as ON, and a failed read prints — losing the customer's
        // proof of refund because tenant storage hiccuped is worse than one
        // unwanted slip.
        try {
            if ((await tenantStorage.getState('printingEnabled')) === false) return;
        } catch (stateErr) {
            console.warn('Refund slip: could not read printingEnabled, assuming on:', stateErr);
        }

        // Only network tenants have a direct-dial transport. usb/none
        // tenants are left exactly as they are — no local silent print, no
        // iframe/print-dialog fallback. Either would be brand new printing
        // behaviour for tenants this change is not about.
        const method = tenantConfig?.printing?.method;
        if (method !== 'network') return;

        const { protocol, printerId } = await resolveNetworkProtocol(method, 'receipt');
        if (!shouldPrintDirect(method, protocol)) return;

        // `printedAtStamp()` is the slip's ONLY clock, taken here at the
        // moment of print. Refund.created_at is datetime.utcnow and the
        // backend has no tenant timezone to convert it with, so under BST
        // the server's own stamp put the refund an hour BEFORE the `Sold:`
        // line beside it on the same slip.
        // The same language the receipt and ticket paths already send. The
        // backend has always accepted it here and passes it into
        // render_refund's `translations`; only the client half was missing,
        // so an Arabic tenant got an English-only refund slip while their
        // receipts and kitchen tickets were translated.
        const language = await receiptLanguage();
        const payload = await printingService.getRefundPayload(
            refundId, printerId || undefined, printedAtStamp(), language,
        );
        if (!payload) {
            console.warn('Refund slip: no payload returned');
            return;
        }

        await sendPayloadAndReport(payload, 'receipt');
    } catch (err) {
        // Covers the payload fetch too: no payload means no PrintJob was
        // created and nothing was sent, so nothing is silently lost.
        console.warn('Refund slip print skipped:', err);
    }
};

// REUSABLE iframe printer - use this everywhere
// The cashier's "Receipt Translations" toggle, as a language the renderer
// can use. Both are device state (SellSurface writes them on toggle), and
// the renderer is server-side, so the language has to travel with each
// print request. Returns null when translations are off — the default —
// which keeps the request identical to what it was and saves the backend a
// lookup it would only throw away.
const receiptLanguage = async () => {
    try {
        const enabled = await tenantStorage.getState('receiptTranslationsEnabled');
        if (!enabled) return null;

        // Two stores, deliberately both. LanguageContext picks the tenant's
        // non-English language and writes it to localStorage; the toggle
        // handler copies it into tenantStorage — but only if it is already
        // there when the toggle is flipped. Flip it before the language
        // lands and tenantStorage never gets one, so the receipt says
        // "translations on" and prints none. Reading both closes that gap
        // and matches what the browser's own renderer reads.
        // Typed check, not merely truthy. This value becomes a query-string
        // parameter, and tenantStorage is schemaless key/value — a stray
        // boolean or number under this key would be serialised into the URL
        // and looked up as a language code, which silently returns no
        // translations at all. A non-string here means "no language chosen".
        const stored = await tenantStorage.getState('receiptTranslationLanguage');
        if (typeof stored === 'string' && stored) return stored;

        const local = localStorage.getItem('receiptTranslationLanguage');
        if (local) return local;

        // Last resort: the tenant's own non-English language. Translations
        // being on with no language chosen is a silent no-op — the receipt
        // reports "translations enabled" and prints none — and there is no
        // way to tell from the till. If the tenant has exactly one other
        // language, that is unambiguously the one meant.
        try {
            const config = JSON.parse(localStorage.getItem('tenantConfig') || '{}');
            const other = (config.languages || []).find((lang) => lang !== 'en');
            return other || null;
        } catch (err) {
            return null;
        }
    } catch (err) {
        // Never let a settings read stop a receipt printing.
        console.warn('Could not read the receipt language, printing untranslated:', err);
        return null;
    }
};

// The receipt as the server renders it, realised as HTML for the browser.
//
// Both surfaces render from one place now. The browser used to build its own
// receipt HTML, which is how the two drifted: on 2026-09-08 a split bill
// printed one receipt on the app and three in the browser, and per-group
// slips could not print at all.
//
// Returns null when there is nothing to fetch or the fetch fails, and the
// caller falls back to whatever HTML it was given. A receipt printing in the
// old layout beats a receipt not printing.
const sharedReceiptHtml = async (config, role) => {
    if (role !== 'receipt') return null;
    const transactionId = config?.transaction_id;
    if (!transactionId) return null;

    try {
        const language = await receiptLanguage();
        // Same local print moment as the printer path above — the browser is
        // a device with a local clock too. Omitting it here is how the
        // `Printed:` line would come back on paper but not on screen.
        const { copies = [], logo_url: logoUrl } =
            await printingService.getReceiptText(transactionId, language, printedAtStamp());
        if (!copies.length) return null;

        return copies
            .map((copy, index) => {
                // Canvas tenants: the server sends the very bitmap the printer
                // head would put on paper, so the browser shows THAT rather
                // than re-deriving HTML from markup. Two renderings of one
                // document is exactly how the surfaces drifted before, and an
                // image cannot drift from itself.
                //
                // No logo <img> on this branch: the logo is already drawn into
                // the bitmap, and adding one would print it twice.
                if (copy.image_base64) {
                    return '<div class="print-page">'
                        + `<img src="data:image/png;base64,${copy.image_base64}" `
                        + 'alt="" class="receipt-raster" style="width:100%" />'
                        + '</div>';
                }
                // Only the first copy carries the logo; a cashier copy is
                // for the till, not the customer.
                const html = starMarkupToHtml(copy.text, index === 0 ? logoUrl : null);
                return `<div class="print-page">${html}</div>`;
            })
            .join('');
    } catch (err) {
        console.warn('Shared receipt text unavailable, printing local layout:', err);
        return null;
    }
};

const printWithIframe = (htmlContent, windowTitle = "Receipt", method = undefined, config = null, role = null, printers = null) => {
    return new Promise(async (resolve, reject) => {
        console.log(`Starting print: ${windowTitle}`);

        const { protocol, printerId } = await resolveNetworkProtocol(method, role, printers);

        // Network tenants: the backend enqueue/poll (CloudPRNT) owns printing.
        // The POS does NO local printing — neither silent nor iframe/dialog.
        // Exception: a network Epson tenant, handled just below.
        if (!shouldPrintAtAll(method, protocol)) {
            console.log(`Network method: skipping local print (backend owns it)`);
            resolve();
            return;
        }

        // Network Epson tenant: the device is the only thing that can print,
        // directly over ePOS-Print — never fall through to local/iframe print
        // either way, success or failure (a failure already has a queued
        // backend job as its fallback; a local print here would double-print
        // a receipt that WILL eventually be handled). `printerId` is the same
        // id resolveNetworkProtocol used to decide `protocol` — see its
        // comment and attemptDirectEpsonPrint's.
        if (shouldPrintDirect(method, protocol)) {
            await attemptDirectEpsonPrint(config, role, printerId);
            resolve();
            return;
        }

        // Everything below prints through the browser's hidden-iframe
        // fallback — no shipping host has a silent-print bridge. The body
        // comes from the server renderer so both surfaces show the same
        // receipt; the caller's HTML is the fallback when there is no
        // transaction to render from (a preview, a reprint of nothing) or
        // the fetch fails.
        const shared = await sharedReceiptHtml(config, role);
        if (shared) {
            htmlContent = shared;
        }

        // Web fallback: hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-1000px';
        iframe.style.left = '-1000px';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.visibility = 'hidden';

        document.body.appendChild(iframe);

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${windowTitle}</title>
                <style>
                    body { margin: 0; padding: 0; font-family: monospace; font-size: 12px; }
                    .print-page { width: 76mm; padding: 2mm; margin: 0 auto; page-break-after: always; }
                    @page { size: 80mm auto; margin: 0; }
                    @media print { 
                        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .print-page { break-inside: avoid; page-break-inside: avoid; }
                    }
                </style>
            </head>
            <body>
                ${htmlContent}
            </body>
            </html>
        `;

        // Write content and print
        iframe.contentDocument.open();
        iframe.contentDocument.write(printContent);
        iframe.contentDocument.close();

        let printCompleted = false;

        // Wait for images to load before printing
        const waitForImagesAndPrint = () => {
            // First ensure the iframe document is ready
            if (!iframe.contentDocument || !iframe.contentDocument.body) {
                console.log(`${windowTitle} iframe document not ready, retrying...`);
                setTimeout(waitForImagesAndPrint, 100);
                return;
            }

            const images = iframe.contentDocument.querySelectorAll('img');
            console.log(`${windowTitle} found ${images.length} images to wait for`);

            // If no images, proceed immediately
            if (images.length === 0) {
                console.log(`${windowTitle} no images found, proceeding with print`);
                proceedWithPrint();
                return;
            }

            const imagePromises = Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        console.log(`${windowTitle} image already loaded:`, img.src);
                        resolve();
                    } else {
                        console.log(`${windowTitle} waiting for image to load:`, img.src);
                        img.onload = () => {
                            console.log(`${windowTitle} image loaded successfully:`, img.src);
                            resolve();
                        };
                        img.onerror = () => {
                            console.log(`${windowTitle} image failed to load:`, img.src);
                            resolve(); // Still resolve even if image fails to load
                        };
                        // Fallback timeout for stubborn images
                        setTimeout(() => {
                            console.log(`${windowTitle} image load timeout:`, img.src);
                            resolve();
                        }, 2000);
                    }
                });
            });

            Promise.all(imagePromises).then(() => {
                console.log(`${windowTitle} all images processed, proceeding with print`);
                proceedWithPrint();
            });
        };

        const proceedWithPrint = () => {
            try {
                // Set up event listeners for print completion
                iframe.contentWindow.addEventListener('beforeprint', () => {
                    console.log(`${windowTitle} print dialog opened`);
                });

                iframe.contentWindow.addEventListener('afterprint', () => {
                    console.log(`${windowTitle} print dialog closed`);
                    // Clear print flag after a brief delay so ESC doesn't cascade
                    setTimeout(() => { window.__aqanaPrintDialogOpen = false; }, 300);
                    if (!printCompleted) {
                        printCompleted = true;
                        setTimeout(() => {
                            document.body.removeChild(iframe);
                            console.log(`${windowTitle} iframe cleaned up after print completion`);
                            resolve();
                        }, 500);
                    }
                });

                window.__aqanaPrintDialogOpen = true;
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                console.log(`${windowTitle} print command sent`);

                // Safety fallback — only if afterprint never fires (30s)
                setTimeout(() => {
                    window.__aqanaPrintDialogOpen = false;
                    if (!printCompleted) {
                        printCompleted = true;
                        try { document.body.removeChild(iframe); } catch (_) {}
                        console.log(`${windowTitle} iframe cleaned up after timeout`);
                        resolve();
                    }
                }, 30000);

            } catch (error) {
                console.error(`${windowTitle} iframe printing failed:`, error);
                window.__aqanaPrintDialogOpen = false;
                document.body.removeChild(iframe);
                reject(error);
            }
        };

        setTimeout(waitForImagesAndPrint, 800); // Back to original timeout
    });
};

/**
 * What to call the thing in the `tableNumber` slot, on PAPER.
 *
 * The decision — which of the three things the slot holds — now lives in
 * utils/tableSlot.js, shared with the refund modal's sale summary. Only the
 * WORDING stays here, because the printed receipt has to match the backend
 * renderers' English literals while the UI runs the same decision through t().
 *
 * Four independent copies of the "#" rule is how a takeaway's "#021" came to
 * be labelled "Table Number" in the refund modal. Mirrors `_table_label` in
 * backend/app/services/receipt_renderer.py; the two must agree, since a tenant
 * hits either this HTML path or a backend renderer.
 */
const SLOT_LABELS = { staff: 'Staff', order: 'Order', table: 'Table' };

const tableSlotLabel = (tableNumber, diningOption) =>
    SLOT_LABELS[tableSlotKind(tableNumber, diningOption)];

// Function to generate receipt HTML - COMPLETELY REWRITTEN
const generateReceiptHTML = (data, title, simplified = false, tenantInfo = null, translationsEnabled = true, receiptTranslationLanguage = '', t = null, dynamicTranslations = null, translationsLoaded = false) => {
    // Safely extract tenant info with defaults
    const brandName = tenantInfo?.brand_name;
    const currency = tenantInfo?.currency;
    const receiptHeader = title;
    const tenantHeader = tenantInfo?.receipt_header;
    const receiptFooter = tenantInfo?.receipt_footer;
    const receiptLogo = tenantInfo?.receipt_logo;

    // Ensure we have valid products array
    const validProducts = Array.isArray(data.products) ? data.products.filter(p => p && p.name) : [];

    // Safely extract data values to prevent undefined
    const receiptData = {
        date: data.date || format(new Date(), 'yyyy-MM-dd'),
        time: data.time || format(new Date(), 'HH:mm:ss'),
        cashier: data.cashier || 'Unknown',
        paymentMethod: data.paymentMethod || '',
        diningOption: data.diningOption || '',
        tableNumber: data.tableNumber || '',
        subtotal: safeNumeric(data.subtotal),
        discount: safeNumeric(data.discount),
        promoDiscount: safeNumeric(data.promoDiscount),
        promoName: data.promoName || '',
        serviceCharge: safeNumeric(data.serviceCharge),
        tax: safeNumeric(data.tax),
        total: safeNumeric(data.total),
        notes: data.notes || '',
        splitInfo: data.splitInfo || null,
        split_payment: data.split_payment || null,
        split_payment_highlight: data.split_payment_highlight || null
    };

    // Create product rows with proper formatting
    const productsHTML = validProducts.length > 0
        ? validProducts.map(product => {
            // Check if we should add Arabic transliteration (only for simplified receipts)
            const transliteration = translationsEnabled && receiptTranslationLanguage ?
                getReceiptTransliteration(product.name, receiptTranslationLanguage, dynamicTranslations, translationsLoaded) : null;
            // Safely get product values
            const productName = product.name || 'Unknown Item';
            const productPrice = safeNumeric(product.selling_price);
            const productQuantity = parseInt(product.quantity) || 1;
            const productTotal = productPrice * productQuantity;

            // First create the main product row
            let productRow = `
<tr>
    <td style="text-align: left; padding: 2px 0; word-break: break-word;">
        ${productName}
        ${transliteration ? `<br><span style="font-size: 0.95em; color: #444;">${transliteration}</span>` : ''}
    </td>
    ${!simplified ?
                    `<td style="text-align: left; padding: 3px 2px 1px 2px; font-size: 12px;">
        ${currency}${productPrice.toFixed(2)}
    </td>` : ''
                }
    <td style="text-align: right; padding: 3px 4px; min-width: 45px; font-weight: bold;">
        ${productQuantity}
    </td>
    ${!simplified ?
                    `<td style="text-align: right; padding: 2px 0;">
        ${currency}${productTotal.toFixed(2)}
    </td>` : ''
                }
</tr>`;

            // Add rows for add-ons if present
            if (product.add_ons && product.add_ons.length > 0) {
                product.add_ons.forEach(addon => {
                    // Check if we should add Arabic transliteration for add-ons
                    const addonTransliteration = translationsEnabled && receiptTranslationLanguage ?
                        getReceiptTransliteration(addon.name, receiptTranslationLanguage, dynamicTranslations, translationsLoaded) : null;
                    const addonPrice = safeNumeric(addon.price);
                    const addonTotal = addonPrice * productQuantity;

                    productRow += `
            <tr>
                <td style="text-align: left; padding: 0 0 2px 10px; font-size: 0.85em; color: #555;">
                    + ${addon.name || 'Add-on'}
                    ${addonTransliteration ? `<br><span style="font-size: 0.95em; color: #444;">${addonTransliteration}</span>` : ''}
                </td>
                ${!simplified ?
                            `<td style="text-align: left; padding: 0 2px 2px 12px; font-size: 10px; color: #555;">
                        ${currency}${addonPrice.toFixed(2)}
                    </td>` : ''
                        }
                <td style="text-align: right; padding: 0 0 2px 0; font-size: 0.85em; color: #555;">
                    ${productQuantity}
                </td>
                ${!simplified ?
                            `<td style="text-align: right; padding: 0 0 2px 0; font-size: 0.85em; color: #555;">
                        ${currency}${addonTotal.toFixed(2)}
                    </td>` : ''
                        }
            </tr>`;
                });
            }

            // If product has notes, add a row for the notes
            if (product.notes && product.notes.trim()) {
                productRow += `
<tr>
    <td colspan="${simplified ? 2 : 4}" style="text-align: left; padding: 0 0 6px 10px; font-style: italic; font-size: 0.85em; color: #555;">
        * ${product.notes}
    </td>
</tr>`;
            }

            return productRow;
        }).join('')
        : `<tr><td colspan="${simplified ? 2 : 4}" style="text-align: center; padding: 4px 0;">No items</td></tr>`;

    // Build payment method display
    let paymentDisplay = '';
    if (!simplified) {
        if (receiptData.split_payment) {
            paymentDisplay = `
            <p>Payment: Split Payment</p>
            <div style="margin: 2px 0;">
                <div style="display: flex; justify-content: space-between;">
                    <span>Cash:</span>
                    <span>${currency}${safeNumeric(receiptData.split_payment.cash_amount).toFixed(2)}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                    <span>Card:</span>
                    <span>${currency}${safeNumeric(receiptData.split_payment.card_amount).toFixed(2)}</span>
                </div>
            </div>`;
        } else if (receiptData.paymentMethod) {
            paymentDisplay = `<p>Payment: ${receiptData.paymentMethod}</p>`;
        }
    }


    // Build the complete receipt HTML
    return `
<style>
    /* Receipt Container */
    .receipt {
        font-family: monospace;
        width: 76mm;
        padding: 3mm;
        font-size: 16px;
        line-height: 1.4;
    }

    /* Header Section */
    .header {
        text-align: center;
        margin-bottom: 6px;
    }
    .header h2 {
        margin: 0 0 4px 0;
        font-size: 20px;
        font-weight: bold;
    }
    .header p {
        margin: 3px 0;
    }

    /* Logo Section */
    .logo-container {
        text-align: center;
        margin-bottom: 4px;
    }
    .logo-image {
        width: 65mm;
        max-width: 100%;
        height: auto;
        margin: 0 auto;
    }

    /* Receipt Title */
    .receipt-title {
        font-weight: bold;
        margin-bottom: 6px;
        letter-spacing: 1px;
        font-size: 14px;
    }

    /* Dividers */
    .divider {
        border-top: 1px dashed #000;
        margin: 6px 0;
    }

    /* Products Table */
    .products-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 15px;
    }
    .products-table th {
        text-align: left;
        font-weight: bold;
        padding: 4px 2px;
        border-bottom: 1px solid #000;
    }
    .products-table th.right {
        text-align: right;
    }
    .products-table td {
    vertical-align: top;
    padding: 1px 0px;
    word-wrap: break-word;
    }

    /* Totals Section */
    .total-row {
        display: flex;
        justify-content: space-between;
        margin: 2px 0;
    }

    /* Footer */
    .footer {
        text-align: center;
        margin-top: 8px;
        font-size: 10px;
    }

    /* Print Styles */
    @media print {
        .receipt {
            page-break-inside: avoid;
        }
        .page-break {
            page-break-after: always;
        }
    }
</style>
<div class="receipt">
    <div class="header">
    ${!simplified ? `
    <div class="logo-container">
        <img src="${receiptLogo}" alt="${brandName}" class="logo-image" onerror="this.style.display='none'" />
    </div>
    ` : ''}
        ${tenantHeader ? `<p style="margin: 0 0 2px 0;">${tenantHeader}</p>` : ''}
        <p class="receipt-title">${receiptHeader}</p>       
        <p>Date: ${receiptData.date} Time: ${receiptData.time}</p>
        <p>Cashier: ${receiptData.cashier}</p>
        ${paymentDisplay}
        <p>Dining: ${receiptData.diningOption}${translationsEnabled && receiptTranslationLanguage && t ? ` - ${translateDynamic(receiptData.diningOption, t)}` : ''}</p>
        ${receiptData.tableNumber ? `<p>${tableSlotLabel(receiptData.tableNumber, receiptData.diningOption)}: ${receiptData.tableNumber}</p>` : ''}
        ${receiptData.splitInfo ? `
<div style="text-align: center; border-top: 1px dashed black; border-bottom: 1px dashed black; padding: 4px 0; margin: 4px 0;">
    <p style="margin: 0; font-weight: bold;">SPLIT BILL ${receiptData.splitInfo.splitNumber} of ${receiptData.splitInfo.totalSplits}</p>
    <p style="margin: 0;">Amount per customer: ${currency}${receiptData.splitInfo.amountPerSplit}</p>
</div>
` : ''}
    </div>
    <div class="divider"></div>
    <table class="products-table">
    <thead>
        <tr>
<th style="width: ${simplified ? '60%' : '32%'}">Item</th>
${!simplified ? `<th style="width: 22%; text-align: left;">Price</th>` : ''}
<th style="width: ${simplified ? '40%' : '14%'}; text-align: right;">Qty</th>
${!simplified ? `<th style="width: 32%; text-align: right;">Total</th>` : ''}
        </tr>
    </thead>
        <tbody>
            ${productsHTML}
        </tbody>
    </table>
    <div class="divider"></div>
    ${!simplified ? `
    <div style="margin-top: 4px;">
        <div style="display: flex; justify-content: space-between;">
            <span>Subtotal:</span>
            <span>${currency}${receiptData.subtotal.toFixed(2)}</span>
        </div>
        ${receiptData.discount > 0 ? `
        <div style="display: flex; justify-content: space-between;">
            <span>Discount:</span>
            <span>-${currency}${receiptData.discount.toFixed(2)}</span>
        </div>
        ` : ''}
        ${receiptData.promoDiscount > 0 ? `
        <div style="display: flex; justify-content: space-between;">
            <span>${receiptData.promoName ? receiptData.promoName : 'Promotion'}:</span>
            <span>-${currency}${receiptData.promoDiscount.toFixed(2)}</span>
        </div>
        ` : ''}
        ${receiptData.serviceCharge > 0 ? `
        <div style="display: flex; justify-content: space-between;">
            <span>Service Charge:</span>
            <span>${currency}${receiptData.serviceCharge.toFixed(2)}</span>
        </div>
        ` : ''}
        ${receiptData.tax > 0 ? `
        <div style="display: flex; justify-content: space-between;">
            <span>Tax:</span>
            <span>${currency}${receiptData.tax.toFixed(2)}</span>
        </div>
        ` : ''}
        <div style="display: flex; justify-content: space-between; font-weight: bold; margin-top: 2px;">
            <span>GRAND TOTAL:</span>
            <span>${currency}${receiptData.total.toFixed(2)}</span>
        </div>
    </div>
    ` : ''}

    ${receiptData.split_payment ? `
        <div style="border-top: 1px dashed #000; margin-top: 6px; padding-top: 6px;">
            <p style="margin: 2px 0; font-weight: bold;">SPLIT PAYMENT:</p>
            <div style="display: flex; justify-content: space-between; ${receiptData.split_payment_highlight === 'cash' ? 'font-weight: bold; background-color: #f0f0f0;' : ''}">
                <span>Cash:</span>
                <span>${currency}${safeNumeric(receiptData.split_payment.cash_amount).toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; ${receiptData.split_payment_highlight === 'card' ? 'font-weight: bold; background-color: #f0f0f0;' : ''}">
                <span>Card:</span>
                <span>${currency}${safeNumeric(receiptData.split_payment.card_amount).toFixed(2)}</span>
            </div>
        </div>
        ` : ''}
        ${receiptData.split_payment_summary ? `
            <div style="border-top: 1px dashed #000; margin-top: 6px; padding-top: 6px;">
                <p style="margin: 2px 0; font-weight: bold;">PAYMENT SUMMARY:</p>
                ${receiptData.split_payment_summary.map(payment => `
                    <div style="display: flex; justify-content: space-between;">
                        <span>Customer ${payment.customer} (${payment.method}):</span>
                        <span>${currency}${safeNumeric(payment.amount).toFixed(2)}</span>
                    </div>
                `).join('')}
            </div>
            ` : ''}
${receiptData.notes ? `
<div style="margin: 6px 0;">
    <p style="margin: 0; font-weight: bold;">Notes:</p>
    <p style="margin: 2px 0; word-wrap: break-word; ${receiptData.notes.includes('Split from') ? 'font-weight: bold; color: #0f5132;' : ''}">${receiptData.notes}</p>
</div>
` : ''}
    <div class="footer">
    ${!simplified ? `<p style="margin: 2px 0;">${receiptFooter}</p>
        <p style="margin: 2px 0;">Glad to serve you.</p>` : ''}
        <p style="margin: 2px 0;">Printed: ${new Date().toLocaleString()}</p>
    </div>
</div>
`;
};

// Function to create print handlers - UPDATED TO BE DYNAMIC
const createPrintHandlers = (refs) => {
    const { fullReceiptRef, reprintReceiptRef } = refs;

    // Generic print handler that can be used for any receipt type
    // TODO: useReactToPrint is a hook called outside a component/hook (rules-of-hooks);
    // createPrintHandlers is currently unused — needs refactor by the printing owner before reuse.
    const createGenericPrintHandler = (ref, documentTitle) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useReactToPrint({
            content: () => ref.current,
            documentTitle: documentTitle,
            copyStyles: true,
            pageStyle: `
@page {
    size: 80mm auto;
    margin: 0mm;
}
@media print {
    body * {
        visibility: hidden;
    }
    .print-receipt, .print-receipt * {
        visibility: visible;
    }
    .print-receipt {
        position: absolute;
        left: 0;
        top: 0;
        width: 80mm;
        margin: 0;
        padding: 5mm;
    }
}`,
            removeAfterPrint: true,
            onAfterPrint: () => console.log(`${documentTitle} printed`),
        });
    };

    const handlePrintFullReceipt = createGenericPrintHandler(fullReceiptRef, 'Customer Receipt');
    const handleReprintReceipt = createGenericPrintHandler(reprintReceiptRef, 'Receipt Reprint');

    // Return handlers - can be extended dynamically
    return {
        handlePrintFullReceipt,
        handleReprintReceipt,
        createGenericPrintHandler // Export this for dynamic creation
    };
};

// Function to select printer and print receipt - UPDATED
const selectPrinter = (setShowPrinterModal, setSelectedPrinter, date, selectedProducts,
    selectedCashier, selectedPaymentMethod, selectedDiningOption, tableNumber,
    notes, calculateTotals, tenantInfo = null) => {

    if (setShowPrinterModal) {
        setShowPrinterModal(false);
    }

    const receiptData = {
        date: format(date, 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm:ss'),
        cashier: selectedCashier,
        paymentMethod: selectedPaymentMethod,
        diningOption: selectedDiningOption,
        tableNumber: tableNumber,
        products: selectedProducts,
        subtotal: calculateTotals().subtotal,
        discount: calculateTotals().discount,
        serviceCharge: calculateTotals().serviceCharge,
        tax: calculateTotals().tax || 0,
        total: calculateTotals().total,
        notes: notes,
    };

    printReceipts(receiptData, selectedProducts, false, tenantInfo);
};

// Customer Receipt Type - Full receipt with prices and totals
const generateCustomerReceipt = (receiptData, title, tenantInfo, translationsEnabled = true, receiptTranslationLanguage = '', receiptT = null, dynamicTranslations = null, translationsLoaded = false) => {
    return generateReceiptHTML(receiptData, title, false, tenantInfo, translationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded);
};

const generateKitchenBarReceipt = (receiptData, title, tenantInfo, translationsEnabled = true, receiptTranslationLanguage = '', receiptT = null, dynamicTranslations = null, translationsLoaded = false) => {
    return generateReceiptHTML(receiptData, title, true, tenantInfo, translationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded);
};

// COMPLETELY REWRITTEN - Dynamic staff allowance printing based on tenant config
const printStaffAllowanceReceipts = (receiptData, products, tenantInfo = null, translationsEnabled = true, receiptTranslationLanguage = '', receiptT = null, dynamicTranslations = null, translationsLoaded = false) => {
    const brandName = tenantInfo?.brandName || 'Restaurant';
    const printableCategories = tenantInfo?.printableCategories || tenantInfo?.printable_categories || [];
    const method = tenantInfo?.printing?.method;

    console.log("Starting staff allowance receipt printing process with translations");
    console.log("Receipt data:", receiptData);
    console.log("Products to print:", products);
    console.log("Printable categories:", printableCategories);
    console.log("Translation settings:", { translationsEnabled, receiptTranslationLanguage, translationsLoaded });

    try {
        const receipts = [];

        // Create receipts for each printable category that has items
        printableCategories.forEach(category => {
            const categoryItems = products.filter(p => p.category === category);
            if (categoryItems.length > 0) {
                console.log(`${category} items found:`, categoryItems.length);
                const categoryData = { ...receiptData, products: categoryItems };
                receipts.push({
                    category: category,
                    html: generateReceiptHTML(
                        categoryData,
                        `${category.toUpperCase()} ORDER - STAFF ALLOWANCE`,
                        true, // simplified
                        tenantInfo,
                        translationsEnabled,
                        receiptTranslationLanguage,
                        receiptT,
                        dynamicTranslations,
                        translationsLoaded
                    ),
                    title: `${category.toUpperCase()} ORDER - STAFF ALLOWANCE`
                });
            }
        });

        // Handle items not in printable categories
        const otherItems = products.filter(p => !printableCategories.includes(p.category));
        if (otherItems.length > 0) {
            console.log("Other items found:", otherItems.length);
            const otherData = { ...receiptData, products: otherItems };
            receipts.push({
                category: "Other",
                html: generateReceiptHTML(
                    otherData,
                    "OTHER ITEMS - STAFF ALLOWANCE",
                    true, // simplified
                    tenantInfo,
                    translationsEnabled,
                    receiptTranslationLanguage,
                    receiptT,
                    dynamicTranslations,
                    translationsLoaded
                ),
                title: "OTHER ITEMS - STAFF ALLOWANCE"
            });
        }

        if (receipts.length === 0) {
            console.log("No specialized receipts to print for staff allowance");
            return;
        }

        console.log(`Ready to print ${receipts.length} translated staff allowance receipts`);

        // NEW: Check if split category printing is enabled
        const splitCategoryPrinting = tenantInfo?.split_category_printing || false;

        if (splitCategoryPrinting) {
            // Print each receipt separately - receipts already have correct structure
            printReceiptsSeparately(receipts, brandName, method, tenantInfo);
        } else {
            // Original behavior: combine all receipts - extract HTML only
            const allReceiptsHTML = receipts.map(receipt => receipt.html);
            printMultipleReceipts(allReceiptsHTML, `${brandName.toUpperCase()} Staff Allowance`, method, tenantInfo, 'receipt');
        }

    } catch (error) {
        console.error("Error in printStaffAllowanceReceipts:", error);
        alert('Error printing receipts: ' + error.message);
    }
};

// COMPLETELY REWRITTEN - True automatic printing using iframe
// Assemble the print job's body from the three things that can make it up.
//
// `stationTickets` are the kitchen/bar slips. They are the CLIENT'S alone —
// the server's copies are the customer-facing transaction receipt and contain
// no station ticket — so they are prepended rather than replaced.
//
// That distinction is the bug this function exists to prevent. Creamio,
// 2026-09-22: a Dine In sale charged straight through printed a two-page job,
// customer receipt and cashier copy, and no bar ticket. The kitchen never
// learned about the order. printReceipts had built [BAR ORDER, CUSTOMER,
// CASHIER] and the combining step was `shared || receipts`, so the moment the
// server copies existed they took the station ticket down with them. Hold
// Order escaped it only because that path passes no role, leaving `shared`
// null.
//
// Station tickets print first: the kitchen wants the slip before the till
// wants the receipt.
export const combineReceiptBodies = (stationTickets, receipts, shared) => {
    const wrap = (html) => `<div class="print-page">${html}</div>`;
    const station = (stationTickets || []).map(wrap).join('');
    return station + (shared || (receipts || []).map(wrap).join(''));
};

const printMultipleReceipts = async (receipts, windowTitle = "Receipts", method = undefined, config = null, role = null, stationTickets = []) => {
    // Both classes count: a job of station tickets and no transaction copy is
    // still a job. Checking `receipts` alone would silently drop the kitchen's
    // slip, which is the failure combineReceiptBodies exists to prevent.
    if ((!receipts || receipts.length === 0)
        && (!stationTickets || stationTickets.length === 0)) {
        console.log("No receipts to print");
        return;
    }

    const { protocol, printerId } = await resolveNetworkProtocol(method, role);

    // Network tenants: the backend enqueue/poll (CloudPRNT) owns printing.
    // The POS does NO local printing — neither silent nor iframe/dialog.
    // Exception: a network Epson tenant, handled just below.
    if (!shouldPrintAtAll(method, protocol)) {
        console.log("Network method: skipping local print (backend owns it)");
        return;
    }

    // Network Epson tenant: the device prints directly over ePOS-Print —
    // never fall through to local/iframe print either way (see printWithIframe
    // for the full reasoning; a failed direct attempt already has a queued
    // backend job as its fallback). `printerId` is the same id that decided
    // `protocol` above — see resolveNetworkProtocol/attemptDirectEpsonPrint.
    if (shouldPrintDirect(method, protocol)) {
        await attemptDirectEpsonPrint(config, role, printerId);
        return;
    }

    // Same as printWithIframe: the server-rendered body wins, the caller's
    // HTML is the fallback. See sharedReceiptHtml. Station tickets are the
    // exception and survive either way — see combineReceiptBodies.
    const shared = await sharedReceiptHtml(config, role);
    const combinedHtml = combineReceiptBodies(stationTickets, receipts, shared);

    // No shipping host has a silent-print bridge, so every method that
    // reaches here (i.e. isn't a network CloudPRNT/Epson tenant, handled
    // above) falls straight through to the iframe fallback below.

    // Web fallback: hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-1000px';
    iframe.style.left = '-1000px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.visibility = 'hidden';

    document.body.appendChild(iframe);

    const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>${windowTitle}</title>
            <style>
                body {
                    margin: 0;
                    padding: 0;
                    font-family: monospace;
                    font-size: 12px;
                }
                .print-page {
                    width: 76mm;
                    padding: 2mm;
                    margin: 0 auto;
                    page-break-after: always;
                }
                @page {
                    size: 80mm auto;
                    margin: 0;
                }
                @media print {
                    body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .print-page {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                }
            </style>
        </head>
        <body>
            ${combinedHtml}
        </body>
        </html>
    `;

    if (!iframe.contentDocument) {
        console.error("Iframe contentDocument not available");
        document.body.removeChild(iframe);
        return;
    }

    iframe.contentDocument.open();
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();

    // Wait for images to load before printing (matches printWithIframe pattern)
    const waitForImagesAndPrint = () => {
        if (!iframe.contentDocument || !iframe.contentDocument.body) {
            console.log(`${windowTitle} iframe document not ready, retrying...`);
            setTimeout(waitForImagesAndPrint, 100);
            return;
        }

        const images = iframe.contentDocument.querySelectorAll('img');
        console.log(`${windowTitle} found ${images.length} images to wait for`);

        if (images.length === 0) {
            proceedWithPrint();
            return;
        }

        const imagePromises = Array.from(images).map(img => {
            return new Promise((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    setTimeout(() => resolve(), 3000);
                }
            });
        });

        Promise.all(imagePromises).then(() => {
            console.log(`${windowTitle} all images loaded, proceeding with print`);
            proceedWithPrint();
        });
    };

    const proceedWithPrint = () => {
        let cleaned = false;
        const cleanup = () => {
            if (cleaned) return;
            cleaned = true;
            try { document.body.removeChild(iframe); } catch (_) {}
            console.log(`${windowTitle} iframe cleaned up`);
        };

        try {
            // Clean up after the browser finishes processing the print job
            iframe.contentWindow.addEventListener('afterprint', () => {
                console.log(`${windowTitle} afterprint fired`);
                setTimeout(cleanup, 500);
            });

            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            console.log(`${windowTitle} print command sent`);

            // Safety fallback — only if afterprint never fires (30s)
            setTimeout(cleanup, 30000);
        } catch (error) {
            console.error(`${windowTitle} iframe printing failed:`, error);
            cleanup();
        }
    };

    setTimeout(waitForImagesAndPrint, 800);
};

const printReceiptsSeparately = async (receipts, brandName, method = undefined, config = null) => {
    console.log(`Printing ${receipts.length} receipts separately`);

    // Resolve the printer list ONCE for this whole print action, not once per
    // receipt inside the loop below. listPrinters() already returns every
    // printer for every role in one call, so every receipt's role lookup can
    // share it — see resolveNetworkProtocol. Only bother for network-method
    // tenants; for usb/none/unset, resolveNetworkProtocol never touches the
    // network regardless of what's passed here.
    let printers = null;
    if (method === 'network') {
        try {
            printers = (await printingService.listPrinters({ timeout: LIST_PRINTERS_TIMEOUT_MS })) || [];
        } catch (err) {
            console.warn('Epson routing: printer lookup failed, defaulting to cloudprnt:', err);
            printers = [];
        }
    }

    for (let i = 0; i < receipts.length; i++) {
        const receipt = receipts[i];
        const title = `${brandName.toUpperCase()} - ${receipt.title}`;

        console.log(`Printing receipt ${i + 1}/${receipts.length}: ${receipt.title}`);

        try {
            await printWithIframe(receipt.html, title, method, config, roleForReceipt(receipt.category), printers);
            console.log(`Receipt ${i + 1} printed successfully`);
        } catch (error) {
            console.error(`Failed to print receipt ${i + 1}:`, error);
        }
    }

    console.log('All receipts printed');
};

const printReceipts = (receiptData, selectedProducts, skipSpecializedReceipts = false, tenantInfo = null, printingEnabled = true, translationsEnabled = true, receiptTranslationLanguage = '', receiptT = null, dynamicTranslations = null, translationsLoaded = false) => {

    const brandName = tenantInfo?.brandName || 'Restaurant';
    const printableCategories = tenantInfo?.printableCategories || tenantInfo?.printable_categories || [];
    const splitCategoryPrinting = tenantInfo?.split_category_printing || false;
    const method = tenantInfo?.printing?.method;

    console.log("Starting print receipts process");
    console.log("Printable categories:", printableCategories);
    console.log("Split category printing:", splitCategoryPrinting);

    const receipts = [];

    // Check if this is a split receipt
    const isSplitReceipt = receiptData.split_payment || receiptData.splitInfo;

    // Add specialized receipts for kitchen/bar only if this is not a split payment receipt
    if (!isSplitReceipt && !skipSpecializedReceipts && printingEnabled) {
        // Create receipts for each printable category that has items
        printableCategories.forEach(category => {
            const categoryItems = selectedProducts.filter(p => p.category === category);
            if (categoryItems.length > 0) {
                const categoryData = { ...receiptData, products: categoryItems };
                receipts.push({
                    category: category,
                    html: generateKitchenBarReceipt(categoryData, `${category.toUpperCase()} ORDER`, tenantInfo, translationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                    title: `${category.toUpperCase()} ORDER`
                });
                console.log(`Added ${category} receipt with ${categoryItems.length} items`);
            }
        });

        // Group all non-printable categories into "OTHERS"
        const otherItems = selectedProducts.filter(p =>
            !printableCategories.includes(p.category)
        );
        if (otherItems.length > 0) {
            const otherData = { ...receiptData, products: otherItems };
            receipts.push({
                category: "Other",
                html: generateKitchenBarReceipt(otherData, "OTHER ITEMS", tenantInfo, translationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                title: "OTHER ITEMS"
            });
            console.log(`Added OTHER ITEMS receipt with ${otherItems.length} items`);
        }
    } else if (!printingEnabled) {
        console.log('Kitchen/bar printing disabled by user preference');
    }

    // Determine title suffix based on split type
    let titleSuffix = '';
    if (receiptData.split_payment) {
        titleSuffix = ' - SPLIT PAYMENT';
    } else if (receiptData.splitInfo) {
        titleSuffix = ` - SPLIT ${receiptData.splitInfo.splitNumber}/${receiptData.splitInfo.totalSplits}`;
    }

    // Add customer receipt
    receipts.push({
        category: "Customer",
        html: generateCustomerReceipt(receiptData, `CUSTOMER RECEIPT${titleSuffix}`, tenantInfo, translationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
        title: `CUSTOMER RECEIPT${titleSuffix}`
    });

    // Add cashier receipt(s)
    if (receiptData.split_payment) {
        // Create separate receipts for each payment method
        if (safeNumeric(receiptData.split_payment.cash_amount) > 0) {
            const cashReceiptData = {
                ...receiptData,
                paymentMethod: 'Cash',
                final_amount: receiptData.split_payment.cash_amount,
                split_payment_highlight: 'cash'
            };
            receipts.push({
                category: "Cashier",
                html: generateCustomerReceipt(cashReceiptData, `CASH PAYMENT - CASHIER COPY`, tenantInfo),
                title: `CASH PAYMENT - CASHIER COPY`
            });
        }

        if (safeNumeric(receiptData.split_payment.card_amount) > 0) {
            const cardReceiptData = {
                ...receiptData,
                paymentMethod: 'Card',
                final_amount: receiptData.split_payment.card_amount,
                split_payment_highlight: 'card'
            };
            receipts.push({
                category: "Cashier",
                html: generateCustomerReceipt(cardReceiptData, `CARD PAYMENT - CASHIER COPY`, tenantInfo),
                title: `CARD PAYMENT - CASHIER COPY`
            });
        }
    } else {
        // Regular single payment method - add standard cashier copy
        receipts.push({
            category: "Cashier",
            html: generateCustomerReceipt(receiptData, `CASHIER COPY${titleSuffix}`, tenantInfo),
            title: `CASHIER COPY${titleSuffix}`
        });
    }

    console.log(`Total receipts to print: ${receipts.length}`);

    // NEW: Check if split category printing is enabled
    if (splitCategoryPrinting) {
        // Print each receipt separately with its own dialog
        printReceiptsSeparately(receipts, brandName, method, tenantInfo);
    } else {
        // Original behavior: combine all receipts into one print dialog.
        //
        // Split into the two document classes before handing them over. The
        // server renders the transaction receipt (customer + cashier copies)
        // and nothing else, so when its copies win they must replace ONLY
        // those — a station ticket has no server equivalent and would simply
        // vanish. See combineReceiptBodies for the sale this went wrong on.
        const isTransactionCopy = (r) => r.category === 'Customer' || r.category === 'Cashier';
        const stationTickets = receipts.filter(r => !isTransactionCopy(r)).map(r => r.html);
        const transactionCopies = receipts.filter(isTransactionCopy).map(r => r.html);
        printMultipleReceipts(
            transactionCopies,
            `${brandName.toUpperCase()} Receipts`,
            method,
            tenantInfo,
            'receipt',
            stationTickets,
        );
    }
};

// COMPLETELY REWRITTEN - Dynamic split bill receipt printing
const printSplitBillReceipts = (receiptData, title = "SPLIT BILL", printCashierCopy = true, fullBillData = null, tenantInfo = null, printCustomerReceipt = true) => {
    const brandName = tenantInfo?.brandName || 'Restaurant';
    const method = tenantInfo?.printing?.method;

    const receipts = [];
    if (printCustomerReceipt) {
        const customerReceipt = generateCustomerReceipt(receiptData, `${title} - CUSTOMER RECEIPT`, tenantInfo);
        receipts.push(customerReceipt);
    }

    if (printCashierCopy) {
        if (fullBillData && fullBillData.payments && Array.isArray(fullBillData.payments)) {
            // Calculate totals by payment method
            const paymentTotals = {};
            fullBillData.payments.forEach(payment => {
                paymentTotals[payment.method] = (paymentTotals[payment.method] || 0) + payment.amount;
            });

            // Create receipts for each payment method used
            Object.entries(paymentTotals).forEach(([method, amount]) => {
                if (amount > 0) {
                    const paymentReceiptData = {
                        ...receiptData,
                        paymentMethod: method,
                        final_amount: amount,
                        total: amount,
                        notes: `${receiptData.notes || ''} - ${method.toUpperCase()} PORTION OF SPLIT BILL`,
                        split_payment_highlight: method.toLowerCase()
                    };
                    receipts.push(generateCustomerReceipt(paymentReceiptData, `${method.toUpperCase()} PAYMENT - CASHIER COPY`, tenantInfo));
                }
            });
        } else if (fullBillData) {
            // Fallback to original behavior if no payment data
            const fullReceiptData = {
                ...receiptData,
                products: fullBillData.products,
                subtotal: safeNumeric(fullBillData.subtotal) || fullBillData.products.reduce((sum, p) => sum + (safeNumeric(p.selling_price) * parseInt(p.quantity || 1)), 0),
                serviceCharge: safeNumeric(fullBillData.serviceCharge),
                total: safeNumeric(fullBillData.total),
                notes: `${receiptData.notes || ''} - FULL BILL`,
                split_payment_summary: fullBillData.payments
            };

            receipts.push(generateCustomerReceipt(fullReceiptData, "FULL BILL - CASHIER COPY", tenantInfo));
        } else {
            // Regular split bill - just add the regular cashier copy
            receipts.push(generateCustomerReceipt(receiptData, `${title} - CASHIER COPY`, tenantInfo));
        }
    }

    printMultipleReceipts(receipts, `${brandName.toUpperCase()} Split Bill Receipt`, method, tenantInfo, 'receipt');
    return true;
};

// NEW FUNCTION - Print only category orders for kitchen/bar
const printCategoryOrdersOnly = (receiptData, selectedProducts, tenantInfo = null) => {
    const brandName = tenantInfo?.brandName || 'Restaurant';
    const printableCategories = tenantInfo?.printableCategories || tenantInfo?.printable_categories || [];
    const method = tenantInfo?.printing?.method;

    console.log("Printing category orders for split payment");

    const receipts = [];

    // Create receipts for each printable category that has items
    printableCategories.forEach(category => {
        const categoryItems = selectedProducts.filter(p => p.category === category);
        if (categoryItems.length > 0) {
            const categoryData = { ...receiptData, products: categoryItems };
            receipts.push(generateKitchenBarReceipt(categoryData, `${category.toUpperCase()} ORDER`, tenantInfo));
            console.log(`Added ${category} receipt with ${categoryItems.length} items`);
        }
    });

    // Handle items not in printable categories
    const otherItems = selectedProducts.filter(p =>
        !printableCategories.includes(p.category)
    );
    if (otherItems.length > 0) {
        const otherData = { ...receiptData, products: otherItems };
        receipts.push(generateKitchenBarReceipt(otherData, "OTHER ITEMS", tenantInfo));
        console.log(`Added OTHER ITEMS receipt with ${otherItems.length} items`);
    }

    if (receipts.length > 0) {
        console.log(`Printing ${receipts.length} category order receipts`);
        printMultipleReceipts(receipts, `${brandName.toUpperCase()} Category Orders`, method, tenantInfo, 'kitchen');
        return true;
    }

    return false;
};

// Debug functions for transaction data - ENHANCED
const debugTransactionData = (transaction) => {
    console.group('Transaction Debug Info');
    console.log('Transaction ID:', transaction.transaction_id);
    console.log('Date:', transaction.date);
    console.log('Time:', transaction.time);
    console.log('Staff:', transaction.staff_name);
    console.log('Payment Method:', transaction.payment_method);
    console.log('Dining Option:', transaction.dining_option);
    console.log('Table Number:', transaction.table_number);

    console.log('Items Array:', transaction.items);

    if (transaction.items && transaction.items.length > 0) {
        console.table(transaction.items);

        console.group('First Item Structure');
        const firstItem = transaction.items[0];
        for (const key in firstItem) {
            console.log(`${key}:`, firstItem[key], typeof firstItem[key]);
        }
        console.groupEnd();
    } else {
        console.warn('No items found in transaction!');
    }

    // Debug numerical values
    console.group('Numerical Values');
    console.log('Total Amount:', transaction.total_amount, typeof transaction.total_amount);
    console.log('Final Amount:', transaction.final_amount, typeof transaction.final_amount);
    console.log('Discount Amount:', transaction.discount_amount, typeof transaction.discount_amount);
    console.log('Service Charge:', transaction.service_charge, typeof transaction.service_charge);
    console.groupEnd();

    console.log('Notes:', transaction.notes);
    console.groupEnd();
};

const debugTransaction = (transaction) => {
    console.log("Full transaction object:", transaction);
    console.log("Products summary:", transaction.products_summary);

    const keys = Object.keys(transaction);
    console.log("All transaction keys:", keys);

    const possibleItemKeys = keys.filter(key =>
        key.includes('item') || key.includes('product')
    );
    console.log("Possible item keys:", possibleItemKeys);

    // Debug specific data structure issues
    console.group('Data Structure Analysis');
    console.log('Has products array:', Array.isArray(transaction.products));
    console.log('Has items array:', Array.isArray(transaction.items));
    console.log('Products length:', transaction.products?.length || 0);
    console.log('Items length:', transaction.items?.length || 0);
    console.groupEnd();
};

export {
    generateReceiptHTML,
    generateCustomerReceipt,
    generateKitchenBarReceipt,
    createPrintHandlers,
    selectPrinter,
    printReceipts,
    printSplitBillReceipts,
    printStaffAllowanceReceipts,
    printMultipleReceipts,
    printWithIframe,
    sharedReceiptHtml,
    // Exported for tests only. The ticket branch's language/stamp wiring is
    // otherwise unreachable without driving a real print dialog, which jsdom
    // cannot do without breaking its own teardown.
    attemptDirectEpsonPrint,
    printCategoryOrdersOnly,
    printReceiptsSeparately,
    printCategoryTicket,
    printRefundSlip,
    resolveDevicePrinter,
    resolveAssignedPrinterId,
    debugTransactionData,
    debugTransaction,
    safeNumeric // Export for use in other components
};